import { useState, useEffect, useMemo, useRef } from 'react';
import {
  listShiftSets,
  listRecurringRules,
  getScheduledShifts,
  dateToISOString,
} from '../api/Attendance/scheduling';
import type {
  ShiftSetView,
  RecurringShiftRuleView,
  ScheduledShiftView,
  ScheduleTemplate,
  ScheduledShiftOccurrence,
} from '../types/scheduling';
import { getScheduleColor, SCHEDULE_COLOR_NONE } from '../utils/scheduleColors';
import { getGoverningRule } from '../views/Attendance/WorkSchedules/governingRule';

// Schedule colours come from utils/scheduleColors.ts, keyed by shift-set id,
// for both the schedule list (dots) and the calendar (card borders), so they match.

/**
 * Colour of one occurrence. A shift with a set takes its set's colour. A shift
 * that only carries a rule id is keyed in a separate (negative) space, so rule 5
 * never borrows the colour of shift set 5, a different schedule.
 */
function getOccurrenceColor(shiftSetId?: number | null, recurringShiftRuleId?: number | null): string {
  if (shiftSetId) return getScheduleColor(shiftSetId);
  if (recurringShiftRuleId) return getScheduleColor(-recurringShiftRuleId);
  return SCHEDULE_COLOR_NONE;
}

function computeRecurringScheduleStatus(
  recurringRules: RecurringShiftRuleView[],
  options?: { now?: Date }
): 'Active' | 'Inactive' {
  const now = options?.now ?? new Date();
  const oneMonthAhead = new Date(now);
  oneMonthAhead.setMonth(oneMonthAhead.getMonth() + 1);

  const rules = Array.isArray(recurringRules) ? recurringRules : [];
  if (rules.length === 0) return 'Active';

  const isRuleActive = (rule: RecurringShiftRuleView) => {
    if ((rule as any)?.isArchived) return false;
    const start = rule?.startDateUtc ? new Date(rule.startDateUtc) : null;
    const end = rule?.endDateUtc ? new Date(rule.endDateUtc) : null;

    const hasValidStart = !!start && !Number.isNaN(start.getTime());
    const hasValidEnd = !!end && !Number.isNaN(end.getTime());

    // Inactive if end date is reached.
    if (hasValidEnd && end!.getTime() <= now.getTime()) return false;
    // Inactive if start date is >= 1 month ahead of now.
    if (hasValidStart && start!.getTime() >= oneMonthAhead.getTime()) return false;
    return true;
  };

  return rules.some(isRuleActive) ? 'Active' : 'Inactive';
}

// ============================================================================
// useScheduleTemplates Hook
// ============================================================================

/**
 * Fetch and combine shift sets with their recurring rules into unified templates.
 * Recurring rules are shown either through their shift sets OR as standalone templates
 * (but never both - this prevents duplicates).
 */
export function useScheduleTemplates() {
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch shift sets and all recurring rules in parallel
      const [shiftSets, allRules] = await Promise.all([
        listShiftSets(),
        listRecurringRules(),
      ]);

      // Track which rules are linked to shift sets (to avoid duplicates)
      const linkedRuleIds = new Set<number>();
      
      // Build set of linked rule IDs from ALL shift sets first
      shiftSets.forEach(set => {
        if (!set.isArchived && set.ruleItems) {
          set.ruleItems.forEach(item => {
            linkedRuleIds.add(item.recurringShiftRuleId);
          });
        }
      });
      
      // Create a map of rules by ID for quick lookup
      const rulesMap = new Map<number, RecurringShiftRuleView>();
      allRules.forEach(rule => {
        if (!rule.isArchived) {
          rulesMap.set(rule.id, rule);
        }
      });

      const allTemplates: ScheduleTemplate[] = [];

      // Convert shift sets to templates (if they have rule items or are meaningful)
      shiftSets
        .filter(set => !set.isArchived)
        .forEach((set) => {
          // Get recurring rules associated with this set via ruleItems, deduplicating by content
          const recurringRulesRaw = (set.ruleItems || [])
            .map(item => rulesMap.get(item.recurringShiftRuleId))
            .filter((rule): rule is RecurringShiftRuleView => rule !== undefined);
          // The dates are part of the key: a split leaves two rules with the same pattern and times but
          // different periods, and dropping the later one hid the period that actually governs the set.
          const seenRuleKeys = new Set<string>();
          const recurringRules = recurringRulesRaw.filter(rule => {
            const key = `${rule.rRule}|${rule.startTimeOfDayMinutes}|${rule.endTimeOfDayMinutes}|${rule.breakDurationMinutes ?? ''}|${rule.startDateUtc ?? ''}|${rule.endDateUtc ?? ''}`;
            if (seenRuleKeys.has(key)) return false;
            seenRuleKeys.add(key);
            return true;
          });

          const embedded = (set as any).embeddedSingleShifts ?? (set as any).embeddedShifts ?? [];

          const template = {
            id: set.id,
            name: set.name,
            description: set.description,
            // Keyed by shift-set id, the same key useScheduledShifts colours the
            // calendar's occurrences with, so a schedule's dot matches its shifts.
            color: getScheduleColor(set.id),
            status: computeRecurringScheduleStatus(recurringRules),
            assignedUserIds: set.assignedUserIds || [],
            assignedUsers: set.assignedUsers || [],
            recurringRules,
            embeddedShifts: embedded,
            stats: {
              totalUsers: set.assignedUserIds?.length || 0,
              activeShifts: 0, // Will be calculated from occurrences
              upcomingChanges: embedded?.length || 0,
            },
            isStandalone: false,
            // Store the linked recurringRuleId for calendar filtering
            linkedRecurringRuleId: getGoverningRule(recurringRules)?.id,
          } as ScheduleTemplate & { linkedRecurringRuleId?: number };

          allTemplates.push(template);
        });

      setTemplates(allTemplates);
    } catch (err) {
      console.error('Failed to load schedule templates:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  return {
    templates,
    loading,
    error,
    refresh: loadTemplates,
  };
}

// ============================================================================
// useScheduledShifts Hook
// ============================================================================

export interface ScheduledShiftsFilters {
  linkedRecurringRuleId?: number;
  shiftSetId?: number;
  userId?: string;
}

export interface UseScheduledShiftsOptions {
  enabled?: boolean;
}

/**
 * Fetch scheduled shifts for a date range with optional filtering.
 * Consolidates shifts by time slot (grouping users assigned to same shift time).
 */
export function useScheduledShifts(
  dateRange: Date[],
  filters?: ScheduledShiftsFilters,
  options?: UseScheduledShiftsOptions
) {
  const [shifts, setShifts] = useState<ScheduledShiftOccurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const enabled = options?.enabled ?? true;

  // Convert date range to ISO strings
  const fromDate = useMemo(() => {
    if (!dateRange || dateRange.length === 0) return null;
    return dateToISOString(dateRange[0]);
  }, [dateRange]);

  const toDate = useMemo(() => {
    if (!dateRange || dateRange.length === 0) return null;
    return dateToISOString(dateRange[dateRange.length - 1]);
  }, [dateRange]);

  // Only the newest request may write. Changing the month, the view or the schedule filter in quick succession
  // starts overlapping requests whose responses can land in any order; without this guard a superseded
  // response landing last replaced the current one (the grid showed the previous month's or the previous
  // filter's shifts under the new header and "Viewing" label), and the first response to land cleared the
  // loading flag while the current request was still in flight. Seen on dev 6afedbf22 by
  // sched-co960-anchor A1: the November grid came back empty and the December grid held exactly November's
  // range (Nov 30 - Dec 3); a traced re-run showed five requests started within 800 ms.
  const requestSeq = useRef(0);

  const loadShifts = async () => {
    const seq = ++requestSeq.current;
    const isCurrent = () => seq === requestSeq.current;
    if (!enabled) {
      setShifts([]);
      setLoading(false);
      return;
    }

    if (!fromDate || !toDate) {
      setShifts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const rawShifts = await getScheduledShifts(fromDate, toDate, {
        userId: filters?.userId,
        linkedRecurringRuleId: filters?.linkedRecurringRuleId,
        shiftSetId: filters?.shiftSetId,
      });
      if (!isCurrent()) return;

      // Transform API response: extract date and format times from ISO datetimes
      const processedShifts = rawShifts.map((shift): ScheduledShiftOccurrence => {
        // Prefer server-provided local fields to avoid client-side timezone issues
        const sourceStart = shift.localStartTime || shift.startTime || '';
        const sourceEnd = shift.localEndTime || shift.endTime || '';
        const sourceBreakStart = (shift as any).localBreakStartTime || (shift as any).breakStartTime || '';
        const sourceBreakEnd = (shift as any).localBreakEndTime || (shift as any).breakEndTime || '';
        const date = sourceStart.split('T')[0];
        const startTime = sourceStart.substring(11, 16); // HH:mm
        const endTime = sourceEnd.substring(11, 16); // HH:mm
        const breakStartTime = sourceBreakStart ? sourceBreakStart.substring(11, 16) : undefined;
        const breakEndTime = sourceBreakEnd ? sourceBreakEnd.substring(11, 16) : undefined;

        // Normalize user identity fields to safe strings
        const normalizeUserName = (value: unknown): string => {
          if (typeof value === 'string') return value;
          if (value && typeof value === 'object') {
            const v = value as { fullName?: string; name?: string; email?: string };
            return v.fullName || v.name || v.email || '';
          }
          return '';
        };

        const userFirstName = typeof (shift as any).userFirstName === 'string' ? (shift as any).userFirstName : '';
        const userName = normalizeUserName((shift as any).userName) || normalizeUserName((shift as any).userFullName);
        const userFullName = normalizeUserName((shift as any).userFullName) || userName;

        return {
          id: shift.id,
          userId: (shift.userId as any)?.toString?.() || (shift.userId as unknown as string),
          userFirstName: userFirstName || undefined,
          userName,
          userFullName,
          date,
          startTime,
          endTime,
          startTimeRaw: sourceStart,
          endTimeRaw: sourceEnd,
          breakStartTime,
          breakStartTimeRaw: sourceBreakStart || undefined,
          breakEndTime,
          breakEndTimeRaw: sourceBreakEnd || undefined,
          timeZoneId: shift.timeZoneId,
          origin: shift.origin,
          isVirtual: shift.isVirtual,
          recurringShiftRuleId: shift.recurringShiftRuleId,
          ruleName: shift.ruleName,
          shiftSetId: shift.shiftSetId,
          shiftSetName: shift.shiftSetName,
          color: getOccurrenceColor(shift.shiftSetId, shift.recurringShiftRuleId),
          breakDurationMinutes: shift.breakDurationMinutes,
          overrideReason: shift.overrideReason,
          // Keep UI contract consistent: a single occurrence has one assigned user.
          assignedUsers: [{
            id: (shift.userId as any)?.toString?.() || (shift.userId as unknown as string),
            name: userName || '',
            fullName: userFullName,
          }],
          userCount: 1,
        };
      });

      // Consolidate ONLY ShiftSet-based occurrences (Rule/ShiftSetEmbedded) when not filtering by user.
      // This keeps manual overrides editable (per-user IDs) while showing one calendar item per template time-slot.
      let consolidatedShifts = processedShifts;
      if (!filters?.userId) {
        const passthrough: ScheduledShiftOccurrence[] = [];
        const grouped = new Map<string, ScheduledShiftOccurrence>();

        for (const s of processedShifts) {
          const isShiftSetOccurrence = !!s.shiftSetId && (s.origin === 'Rule' || s.origin === 'ShiftSetEmbedded');
          if (!isShiftSetOccurrence) {
            passthrough.push(s);
            continue;
          }

          // Key excludes isVirtual so materialized + virtual at the same slot merge into one entry.
          const key = [
            String(s.shiftSetId),
            s.origin,
            s.date,
            s.startTime,
            s.endTime,
          ].join('|');

          const existing = grouped.get(key);
          if (!existing) {
            grouped.set(key, {
              ...s,
              // mark as aggregated when it potentially represents multiple users
              isAggregated: true,
              assignedUsers: (s.assignedUsers || []).slice(),
              userCount: (s.assignedUsers || []).length || 1,
            } as any);
            continue;
          }

          // Prefer materialized over virtual (materialized has a real id)
          if (!s.isVirtual && existing.isVirtual) {
            const prevUsers = existing.assignedUsers || [];
            const newUsers = (s.assignedUsers || []).slice();
            for (const u of prevUsers) {
              if (!newUsers.some(m => m.id === u.id)) newUsers.push(u);
            }
            grouped.set(key, {
              ...s,
              isAggregated: true,
              assignedUsers: newUsers,
              userCount: newUsers.length,
            } as any);
            continue;
          }

          const existingUsers = existing.assignedUsers || [];
          const addUsers = s.assignedUsers || [];
          const merged = [...existingUsers];
          for (const u of addUsers) {
            if (!merged.some(m => m.id === u.id)) merged.push(u);
          }
          (existing as any).assignedUsers = merged;
          (existing as any).userCount = merged.length;
          (existing as any).isAggregated = true;

          // If aggregation combines multiple real IDs, avoid exposing a misleading single ID.
          if (existing.id !== s.id && existing.id !== 0) {
            (existing as any).id = 0;
          }
        }

        consolidatedShifts = [...passthrough, ...Array.from(grouped.values())];
      }

      // Apply filters
      if (filters?.linkedRecurringRuleId || filters?.shiftSetId) {
        // Filter by template: match either the linked recurring rule ID or the shift set ID
        consolidatedShifts = consolidatedShifts.filter(
          shift => 
            (filters.linkedRecurringRuleId && shift.recurringShiftRuleId === filters.linkedRecurringRuleId) ||
            (filters.shiftSetId && shift.shiftSetId === filters.shiftSetId)
        );
      }

      if (filters?.userId) {
        // When filtering by user, show only shifts that include this user
        consolidatedShifts = consolidatedShifts.filter(
          shift => shift.assignedUsers?.some(u => u.id === filters.userId)
        );
      }

      setShifts(consolidatedShifts);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Failed to load scheduled shifts:', err);
      setError(err as Error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    loadShifts();
  }, [enabled, fromDate, toDate, filters?.linkedRecurringRuleId, filters?.shiftSetId, filters?.userId]);

  // Group shifts by date for easier rendering
  const shiftsByDate = useMemo(() => {
    const grouped = new Map<string, ScheduledShiftOccurrence[]>();
    shifts.forEach(shift => {
      const dateKey = shift.date;
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(shift);
    });
    return grouped;
  }, [shifts]);

  return {
    shifts,
    shiftsByDate,
    loading,
    error,
    refresh: loadShifts,
  };
}

// ============================================================================
// useUsers Hook (placeholder - may already exist elsewhere)
// ============================================================================

export interface User {
  id: string;
  name: string;
  fullName?: string;
  department?: string;
  email?: string;
}

/**
 * Fetch list of users (implementation depends on existing user API)
 * This is a placeholder - replace with actual user fetching logic
 */
export function useUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // TODO: Replace with actual user API call
    // For now, return empty array and let component handle it
    const loadUsers = async () => {
      try {
        setLoading(true);
        // const response = await fetchUsers(); // Implement this
        // setUsers(response);
        setUsers([]); // Placeholder
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  return {
    users,
    loading,
    error,
  };
}
